import type { LabelInValue, OrganizationList } from 'models';
import { type ComponentProps, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
import { useOrganizationsInfiniteQuery } from '../../queries';

type P<T extends LabelInValue> = {
  createOption: (value: OrganizationList) => T;
  query?: Partial<Parameters<typeof useOrganizationsInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const OrganizationSelect = <T extends LabelInValue>({ createOption, query, ...props }: P<T>) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);

  const {
    data: organizations,
    fetchNextPage: fetchNextPageOfOrganizations,
    hasNextPage: hasMoreOrganizations,
    isFetching: isFetchingOrganizations,
  } = useOrganizationsInfiniteQuery({
    filter: { search: value, ...query?.filter },
    pagination: { pageSize: 10, ...query?.pagination },
  });

  const options = useMemo(
    () => organizations?.pages.flatMap((page) => page.data.map((organization) => createOption(organization))),
    [createOption, organizations],
  );

  return (
    <InfinityScrollSelect<T>
      handleRequestMoreData={fetchNextPageOfOrganizations}
      hasMoreData={hasMoreOrganizations}
      loading={isFetchingOrganizations || isDebouncing}
      onSearch={setSearch}
      options={options}
      {...props}
    />
  );
};

export default OrganizationSelect;
